import React, { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import SidebarClient from './SidebarClient';
import { motion } from 'framer-motion';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
};

const TAB_ROUTES = [
  '/panel-cliente/dashboard',
  '/panel-cliente/profile',
  '/panel-cliente/explore',
  '/panel-cliente/appointments',
  '/panel-cliente/bonus',
];

const ClientErrorFallback = () => (
  <div className="flex flex-col items-center justify-center h-full py-24 text-center px-6">
    <p className="text-lg font-semibold text-gray-700 mb-2">
      Ha ocurrido un error en el panel de cliente
    </p>
    <p className="text-sm text-gray-400 mb-6">
      Nuestro equipo ha sido notificado automáticamente.
    </p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-[#103254] text-white rounded-lg text-sm hover:opacity-90"
    >
      Recargar página
    </button>
  </div>
);

const LayoutClient: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('id_usuario') || '';

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const ignorarGesto = useRef(false);

  useEffect(() => {
    if (!userId) navigate('/');
  }, [userId, navigate]);

  // Los eventos táctiles burbujean hasta el <main>, así que un gesto
  // horizontal dentro de un selector de fecha, un carrusel o un calendario
  // acababa cambiando de pestaña. Se marcan esas zonas con data-no-swipe
  // (y se excluyen los diálogos) para dejarles su propio gesto.
  const handleTouchStart = (e: React.TouchEvent) => {
    const origen = e.target as HTMLElement | null;
    ignorarGesto.current = Boolean(
      origen?.closest?.('[data-no-swipe], [role="dialog"], .fc')
    );

    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (ignorarGesto.current) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;

    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const currentIndex = TAB_ROUTES.findIndex((r) =>
      r === '/panel-cliente'
        ? location.pathname === r
        : location.pathname.startsWith(r)
    );
    if (currentIndex === -1) return;

    const nextIndex =
      dx < 0
        ? Math.min(currentIndex + 1, TAB_ROUTES.length - 1)
        : Math.max(currentIndex - 1, 0);

    if (nextIndex !== currentIndex) navigate(TAB_ROUTES[nextIndex]);
  };

  return (
    <div className="min-h-[100dvh] flex relative bg-gray-50 dark:bg-black overflow-x-hidden">
      <SidebarClient />

      <motion.main
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        className="flex-1 min-w-0 w-full max-w-full px-4 py-4 sm:px-6 md:px-8 pb-28 lg:pb-4 bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-100 overflow-x-hidden"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Sentry.ErrorBoundary fallback={<ClientErrorFallback />}>
          <Outlet />
        </Sentry.ErrorBoundary>
      </motion.main>
    </div>
  );
};

export default LayoutClient;